import { differenceInDays, isBefore, isValid, parse } from 'date-fns';
import { z } from 'zod';
import { COUNTRIES } from '@src/constants/countries';
import { Gender, MaritalStatus } from '@src/generated/graphql';
import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS } from '../constants/options';

const validateDateTest = (dateStr?: string): boolean => {
  try {
    if (!dateStr) {
      return true;
    }
    const result = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(result);
  } catch {
    return false;
  }
};

const dateIsAtLeast1WeekFromNow = (dateStr: string): boolean => {
  try {
    const testedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    return differenceInDays(testedDate, new Date()) > 7;
  } catch {
    return false;
  }
};

const dateIsBeforeToday = (dateStr: string): boolean => {
  try {
    const testedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isBefore(testedDate, new Date().setHours(0, 0, 0, 0));
  } catch {
    return false;
  }
};

// t('validation.required', 'This field is required')

const requiredString = z.string().min(1, 'validation.required');
const requiredInt = z.number().int().min(1, 'validation.required');

/* t('validation.required_no_special_chars', `Please replace the non-Latin character with its Latin equivalent. <a href="https://support.bordr.com/article/210-what-if-my-name-or-address-has-characters-that-are-not-in-the-latin-alphabet">Learn more</a>`)
 */
const stringWithoutSpecialChars = z
  .string()
  .regex(/^[^ßøŁł]*$/, 'raw:validation.required_no_special_chars');
const requiredStringWithoutSpecialChars = stringWithoutSpecialChars.min(
  1,
  'validation.required',
);

const identityDocumentType = z.enum(['PASSPORT', 'EU_ID'], {
  errorMap: () => ({
    message: 'validation.required',
  }),
});

// t('validation.date', 'Enter a valid date')
// t('validation.birth_date', 'Invalid birth date')
const birthDate = z
  .string({ required_error: 'validation.date' })
  .refine(validateDateTest, 'validation.date')
  .refine(dateIsBeforeToday, 'validation.birth_date');

// t('validation.document_about_to_expire', 'The document has expired or is about to expire')
// should be here the passport expiration date?
const documentExpiration = z
  .string({
    required_error: 'validation.date',
  })
  .refine(validateDateTest, 'validation.date')
  .refine(dateIsAtLeast1WeekFromNow, 'validation.document_about_to_expire');

const country = z.enum(COUNTRIES.map((c) => c.code) as [string, ...string[]], {
  errorMap: () => ({ message: 'validation.required' }),
});

const gender = z.enum(Object.keys(GENDER_OPTIONS) as [Gender, ...Gender[]], {
  errorMap: () => ({ message: 'validation.required' }),
});

const maritalStatus = z.enum(
  Object.keys(MARITAL_STATUS_OPTIONS) as [MaritalStatus, ...MaritalStatus[]],
  {
    errorMap: () => ({ message: 'validation.required' }),
  },
);

// t('validation.phone_number', 'Enter a valid phone number')
const phoneNumber = z
  .string()
  .regex(/^\+?\d{1,3}[- ]?[\d ]{6,20}$/, 'validation.phone_number')
  .min(1, 'validation.required');

// t('validation.email', 'Enter a valid email address')
const email = z.string().email('validation.email');

const requiredUuid = z
  .string()
  .min(1, 'validation.required')
  .uuid('validation.required');

// t('validation.checkbox_required', 'Please confirm to proceed')
const requiredCheckbox = z.literal(true, {
  errorMap: () => ({
    message: 'validation.checkbox_required',
  }),
});

export const VALIDATIONS = {
  requiredString,
  requiredStringWithoutSpecialChars,
  stringWithoutSpecialChars,
  requiredInt,
  birthDate,
  identityDocumentType,
  documentExpiration,
  country,
  gender,
  maritalStatus,
  phoneNumber,
  email,
  requiredUuid,
  requiredCheckbox,
};
